<template>
  <div class="upload-container">
    <div class="upload-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">📹</div>
          <h1>视频上传</h1>
        </div>
      </div>
    </div>

    <!-- 当前授权店铺信息 -->
    <div class="current-shop-section" v-if="advertiserName">
      <div class="shop-info-container">
        <div class="shop-icon">🏪</div>
        <div class="shop-details">
          <h3 class="shop-name">当前授权店铺</h3>
          <p class="shop-advertiser">{{ advertiserName }}</p>
        </div>
      </div>
    </div>

    <!-- 水平排列的输入框组 -->
    <div class="input-group-section">
      <div class="input-group-container">
        <!-- 千川户ID -->
        <div class="input-group">
          <label for="qianchuan-account-id" class="input-group-label">千川户ID：</label>
          <el-select
            id="qianchuan-account-id"
            v-model="qianchuanAccountId"
            filterable
            placeholder="请选择千川户"
            class="input-group-select"
            :disabled="advertiserLoading"
            :popper-append-to-body="true"
            @change="onAdvertiserChange"
            required
          >
            <el-option v-for="item in advertiserOptions" :key="item.advertiserId" :label="item.name" :value="item.advertiserId" />
          </el-select>
          <span class="input-group-hint">* 必须输入</span>
        </div>
        
        <!-- 计划ID -->
        <div class="input-group">
          <label for="plan-id" class="input-group-label">计划ID：</label>
          <el-select
            id="plan-id"
            v-model="planId"
            filterable
            placeholder="请选择计划ID"
            class="input-group-select"
            :disabled="jobLoading || !qianchuanAccountId"
            :popper-append-to-body="true"
            @change="onPlanChange"
          >
            <el-option v-for="item in jobOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </div>
        
        <!-- 商品ID -->
        <div class="input-group">
          <label for="productId" class="input-group-label">商品ID：</label>
          <input
            id="productId"
            v-model="productId"
            type="text"
            placeholder="选择计划后自动带出"
            class="input-group-input"
            readonly
          />
        </div>
      </div>
    </div>
    <!-- 文件上传区域 -->
    <div class="upload-methods-section">
      <div class="methods-container">
        <h2 class="methods-title">文件批量上传</h2>
        <div class="methods-grid">
          <!-- 文件上传区域 -->
          <div class="method-card file-upload-card">
            <div class="card-header">
              <div class="card-icon">📁</div>
              <h3 class="card-title">文件批量上传</h3>
            </div>
            <div class="card-content">
              <p class="card-description">选择多个视频文件进行批量上传</p>
              
              <!-- 文件列表 -->
              <div class="file-list-section" v-if="selectedFiles.length > 0">
                <h4 class="file-list-title">已选择文件 ({{ selectedFiles.length }} 个)</h4>
                <div class="file-list">
                  <div 
                    v-for="(file, index) in selectedFiles" 
                    :key="file.id" 
                    class="file-item"
                    :class="{ 'uploading': file.status === 'uploading', 'success': file.status === 'success', 'error': file.status === 'error' }"
                  >
                    <div class="file-info">
                      <div class="file-name">{{ file.name }}</div>
                      <div class="file-size">{{ formatFileSize(file.size) }}</div>
                    </div>
                    <div class="file-actions">
                      <div class="file-progress" v-if="file.status === 'uploading'">
                        <div class="progress-text">{{ file.progress }}%</div>
                        <div class="progress-bar">
                          <div class="progress-bar-inner" :style="{ width: file.progress + '%' }"></div>
                        </div>
                      </div>
                      <span class="status-icon" v-else-if="file.status === 'success'">✅</span>
                      <span class="status-icon" v-else-if="file.status === 'error'">❌</span>
                      <button 
                        @click="removeFile(index)" 
                        class="remove-button"
                        :disabled="isUploading"
                      >
                        移除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 文件选择区域 -->
              <div class="file-input-section">
                <input
                  type="file"
                  ref="fileInput"
                  multiple
                  accept="video/*"
                  @change="handleFileSelect"
                  style="display: none"
                />
                <div 
                  class="drop-zone"
                  @click="selectFiles"
                  @drop="handleDrop"
                  @dragover.prevent
                  @dragenter.prevent
                >
                  <div class="drop-zone-content">
                    <div class="drop-icon">📁</div>
                    <h4>点击选择文件或拖拽文件到这里</h4>
                    <p class="drop-hint">支持多选，多文件同时上传</p>
                  </div>
                </div>
                
                <button @click="uploadFiles" class="file-upload-button" :disabled="isUploading || selectedFiles.length === 0">
                  {{ isUploading ? '上传中...' : '开始上传' }}
                </button>
                <button @click="clearFiles" class="clear-button" :disabled="isUploading">
                  清空列表
                </button>
              </div>
              
              <!-- 上传进度 -->
              <div class="upload-progress-section" v-if="isUploading">
                <div class="progress-info">
                  <div class="progress-text">
                    整体上传进度: {{ folderProgress }}%
                  </div>
                  <div class="progress-details">
                    已处理: {{ processedFiles }} / {{ totalFiles }} 个文件
                  </div>
                </div>
                <div class="progress-bar">
                  <div class="progress-bar-inner" :style="{ width: folderProgress + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 上传统计信息 -->
    <div class="upload-summary-section" v-if="totalFiles > 0">
      <div class="summary-container">
        <h3 class="summary-title">上传统计</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">总文件数</div>
            <div class="summary-value">{{ totalFiles }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">成功上传</div>
            <div class="summary-value">{{ successfulUploads }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">上传用时</div>
            <div class="summary-value">{{ formatUploadTime() }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="background-animation">
      <div class="upload-waves"></div>
      <div class="floating-element element-1"></div>
      <div class="floating-element element-2"></div>
      <div class="floating-element element-3"></div>
    </div> -->
  </div>
</template>

<script>
import SparkMD5 from 'spark-md5';
import axios from 'axios';
import API_CONFIG from '@/api/config'

export default {
  name: 'UploadVideo',
  data() {
    return {
      selectedFiles: [],
      isUploading: false,
      qianchuanAccountId: '',
      accessToken: '',
      advertiserName: '',
      username: '',
      cardNumber: '',
      advertiserOptions: [],
      advertiserLoading: false,
      planId: '',
      jobOptions: [],
      jobLoading: false,
      productId: '',
      uploadProgress: {},
      totalFiles: 0,
      successfulUploads: 0,
      uploadTime: 0,
      uploadHistory: [],
      currentBatchId: '', // 当前批次ID
      uploadTimer: null, // 上传计时器
      uploadStartTime: 0, // 上传开始时间
      videoLibrary: [
        {
          id: 1,
          name: '示例视频1.mp4',
          size: '25.3 MB',
          duration: '00:02:30',
          date: '2024-01-15'
        },
        {
          id: 2,
          name: '产品展示.mp4',
          size: '18.7 MB',
          duration: '00:01:45',
          date: '2024-01-14'
        }
      ],
      successfulMaterials: [] // 存储成功上传的视频信息，用于批量提交
    }
  },
  
  created() {
    // 从路由参数中获取accessToken和advertiserName
    const query = this.$route.query;
    if (query.accessToken) {
      this.accessToken = query.accessToken;
      localStorage.setItem('accessToken', query.accessToken);
    }
    if (query.advertiserName) {
      this.advertiserName = query.advertiserName;
      localStorage.setItem('advertiserName', query.advertiserName);
    }
    if (query.username) {
      this.username = query.username;
      localStorage.setItem('username', query.username);
    }
    if (query.cardNumber) {
      this.cardNumber = query.cardNumber;
      localStorage.setItem('cardNumber', query.cardNumber);
    }
    
    // 如果路由参数中没有，尝试从localStorage获取
    if (!this.accessToken) {
      this.accessToken = localStorage.getItem('accessToken') || '';
    }
    if (!this.advertiserName) {
      this.advertiserName = localStorage.getItem('advertiserName') || '';
    }
    if (!this.username) {
      this.username = localStorage.getItem('username') || '';
    }
    if (!this.cardNumber) {
      this.cardNumber = localStorage.getItem('cardNumber') || localStorage.getItem('username') || '';
    }
    
    console.log('路由参数:', query);
    console.log('accessToken:', this.accessToken);
    console.log('advertiserName:', this.advertiserName);
    this.updatePageTitle()
    console.log('输入框默认值已清空');
    if (this.accessToken) {
      this.fetchAdvertiserOptions()
    }
  },
  
  methods: {
    updatePageTitle() {
      const name = String(this.advertiserName || '').trim()
      document.title = name ? `${name} - 视频上传` : '视频上传'
    },
    goBack() {
      this.$router.push('/home')
    },
    selectFiles() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.addFiles(files)
    },
    handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files)
      this.addFiles(files)
    },
    async fetchAdvertiserOptions() {
      this.advertiserLoading = true
      try {
        const response = await axios.get(API_CONFIG.getFullUrl('/qianchuan/info/api/getShopAdvertiserPublicSimpleInfo'), {
          params: {
            accessToken: this.accessToken
          },
          headers: {
            username: this.username,
            ...this.getCardNumberHeaders()
          }
        })
        const list = response.data && response.data.code === 200 ? (response.data.data || []) : []
        const rawAdvertiserIds = this.extractRawFieldValues(response, 'advertiser_id')
        this.advertiserOptions = list.map((item, index) => ({
          name: item.id || '-',
          advertiserId: rawAdvertiserIds[index] || item.advertiser_id
        })).filter(item => item.advertiserId)
      } catch (error) {
        console.error('获取千川户信息失败:', error)
        this.advertiserOptions = []
      } finally {
        this.advertiserLoading = false
      }
    },
    async onAdvertiserChange() {
      this.planId = ''
      this.productId = ''
      this.jobOptions = []
      if (!this.qianchuanAccountId) {
        return
      }
      this.jobLoading = true
      try {
        const response = await axios.get(API_CONFIG.getFullUrl('/qianchuan/info/api/getShopAdvertiserJobInfo'), {
          params: {
            accessToken: this.accessToken,
            advertiserId: this.qianchuanAccountId
          },
          headers: {
            username: this.username,
            ...this.getCardNumberHeaders()
          }
        })
        const list = response.data && response.data.code === 200 ? (response.data.data || []) : []
        const rawProductIds = this.extractRawFieldValues(response, 'product_id')
        this.jobOptions = list.map((item, index) => ({
          id: item.id,
          name: item.name || String(item.id || '-'),
          productId: rawProductIds[index] || item.product_id,
          productName: item.product_name || ''
        })).filter(item => item.id)
      } catch (error) {
        console.error('获取计划信息失败:', error)
        this.jobOptions = []
      } finally {
        this.jobLoading = false
      }
    },
    onPlanChange() {
      const selectedJob = this.jobOptions.find(item => String(item.id) === String(this.planId))
      this.productId = selectedJob ? selectedJob.productId : ''
    },
    extractRawFieldValues(response, fieldName) {
      const rawText = response?.request?.responseText
      if (!rawText || typeof rawText !== 'string') {
        return []
      }
      const pattern = new RegExp(`"${fieldName}"\\s*:\\s*(\\d+)`, 'g')
      const values = []
      let match = pattern.exec(rawText)
      while (match) {
        values.push(match[1])
        match = pattern.exec(rawText)
      }
      return values
    },
    addFiles(files) {
      const newFiles = files.map(file => ({
        id: SparkMD5.hash(file.name + file.size + file.lastModified),
        file: file,
        name: file.name,
        size: file.size,
        status: 'pending', // 'pending', 'uploading', 'success', 'error'
        progress: 0,
        errorMessage: ''
      }));

      newFiles.forEach(newFile => {
        if (!this.selectedFiles.some(existingFile => existingFile.id === newFile.id)) {
          this.selectedFiles.push(newFile);
        }
      });
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1)
    },
    clearFiles() {
      this.selectedFiles = [];
      this.totalFiles = 0;
      this.successfulUploads = 0;
      this.processedFiles = 0;
      this.uploadTime = 0;
      this.folderProgress = 0;
      this.successfulMaterials = []; // 清空已存储的素材信息
      this.isUploading = false; // 确保重置上传状态
      if (this.uploadTimer) {
        clearInterval(this.uploadTimer);
        this.uploadTimer = null;
      }
    },


    async uploadFiles() {
      if (this.isUploading) return;

      if (!this.qianchuanAccountId.trim()) {
        alert('请先输入千川户ID才能上传视频');
        return;
      }
      if (!this.accessToken) {
        alert('未找到Access Token，请先授权');
        return;
      }

      this.isUploading = true;
      this.totalFiles = this.selectedFiles.length;
      this.successfulUploads = 0;
      this.processedFiles = 0;
      this.folderProgress = 0;
      this.uploadTime = 0;
      
      this.uploadStartTime = Date.now();
      this.uploadTimer = setInterval(() => {
        this.uploadTime = ((Date.now() - this.uploadStartTime) / 1000).toFixed(2);
      }, 100);

      const filesToUpload = this.selectedFiles.filter(f => f.status === 'pending' || f.status === 'error');
      if (filesToUpload.length === 0) {
        this.isUploading = false;
        if (this.uploadTimer) {
          clearInterval(this.uploadTimer);
          this.uploadTimer = null;
        }
        alert('没有需要上传的文件。');
        return;
      }

      const batchSize = 5;
      const batchIntervalMs = 1500;
      const skippedNoResponseFiles = [];

      const upload = async (fileWrapper, retryCount = 0) => {
        fileWrapper.hasResponse = false;
        fileWrapper.status = 'uploading';
        fileWrapper.progress = 0;
        try {
          const formData = new FormData();
          formData.append('video_file', fileWrapper.file);
          formData.append('advertiser_id', this.qianchuanAccountId);
          formData.append('filename', fileWrapper.name);
          formData.append('plan_id', this.planId);

          const response = await axios.post('/api/video/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Token': this.accessToken,
              username: this.username
            },
            onUploadProgress: progressEvent => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              fileWrapper.progress = percentCompleted;
            }
          });
          fileWrapper.hasResponse = true;

          const imageCode = response?.data?.image?.code
          const videoCode = response?.data?.video?.code
          if (imageCode === 0 && videoCode === 0) {
            fileWrapper.status = 'success';
            fileWrapper.progress = 100;
            this.successfulUploads++;
            
            // 存储成功上传的视频信息，用于后续批量提交
            this.successfulMaterials.push({
              advertiser_id: this.qianchuanAccountId,
              video_id: response.data.video.data.video_id,
              video_cover_id: response.data.image.data.id,
              ad_id: this.planId,
              access_token: this.accessToken,
              product_id: this.productId
            });
          } else {
            // 如果返回码不为0，且还有重试机会，则重试一次
            if (retryCount < 1) {
              console.log(`上传失败，正在重试第${retryCount + 1}次:`, fileWrapper.name);
              return await upload(fileWrapper, retryCount + 1);
            } else {
              fileWrapper.status = 'error';
              fileWrapper.errorMessage = response?.data?.message || `上传失败(image.code=${imageCode}, video.code=${videoCode})`;
            }
          }
        } catch (error) {
          // 网络错误也重试一次
          if (retryCount < 1) {
            console.log(`网络错误，正在重试第${retryCount + 1}次:`, fileWrapper.name);
            return await upload(fileWrapper, retryCount + 1);
          } else {
            fileWrapper.status = 'error';
            fileWrapper.errorMessage = '上传失败';
            console.error('Upload failed:', error);
          }
        } finally {
          // 只有在不是重试的情况下才增加processedFiles计数
          // 避免重试时重复计数导致进度条超过100%
          if (retryCount === 0) {
            this.processedFiles++;
            this.folderProgress = Math.round((this.processedFiles / this.totalFiles) * 100);
          }
        }
      };

      const processByBatch = async () => {
        for (let start = 0; start < filesToUpload.length; start += batchSize) {
          const currentBatch = filesToUpload.slice(start, start + batchSize);
          await Promise.all(currentBatch.map(fileWrapper => upload(fileWrapper)));
          const currentNoResponseFiles = currentBatch.filter(fileWrapper => fileWrapper.hasResponse !== true);
          if (currentNoResponseFiles.length > 0) {
            skippedNoResponseFiles.push(...currentNoResponseFiles);
            console.log('当前批次存在无返回值请求，已跳过这些视频并继续后续批次上传');
          }
          const hasNextBatch = start + batchSize < filesToUpload.length;
          if (hasNextBatch) {
            await this.sleep(batchIntervalMs);
          }
        }
      };

      await processByBatch();
      this.isUploading = false;
      if (this.uploadTimer) {
        clearInterval(this.uploadTimer);
        this.uploadTimer = null;
      }
      this.uploadTime = ((Date.now() - this.uploadStartTime) / 1000).toFixed(2);

      if (this.successfulMaterials.length > 0) {
        console.log('所有视频上传完成，开始批量提交素材信息');
        this.submitMaterialsBatch();
      } else {
        console.log('所有视频上传完成，但没有成功上传的素材需要提交');
      }
      if (skippedNoResponseFiles.length > 0) {
        const names = skippedNoResponseFiles.map(item => item.name).join('、');
        alert(`存在无返回值视频，已跳过并继续上传后续视频：${names}`);
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    previewVideo(video) {
      alert(`预览视频: ${video.name}`);
    },
    deleteVideo(video) {
      if (confirm(`确定要删除视频 "${video.name}" 吗？`)) {
        const index = this.videoLibrary.findIndex(v => v.id === video.id);
        if (index !== -1) {
          this.videoLibrary.splice(index, 1);
        }
      }
    },
    // 格式化上传时间
    formatUploadTime() {
      const seconds = this.uploadTime;
      const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
      const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
      const s = Math.floor(seconds % 60).toString().padStart(2, '0');
      return `${h}:${m}:${s}`;
    },
    
    // 批量提交素材信息（POST请求）- 最终一次性提交
    async submitMaterialsBatch() {
      if (this.successfulMaterials.length === 0) {
        console.log('没有需要提交的素材信息');
        return;
      }
      
      try {
        const total = this.successfulMaterials.length;
        console.log('开始一次性提交素材信息，总数:', total);

        const response = await axios.post(API_CONFIG.addMaterials(), {
          materials: this.successfulMaterials,
          advertiser_id: this.qianchuanAccountId,
          product_id: this.productId,
          ad_id: this.planId,
          access_token: this.accessToken
        }, {
          headers: {
            'Content-Type': 'application/json',
            username: this.username,
            ...this.getCardNumberHeaders()
          }
        });

        console.log('一次性提交结果:', response.data);

        if (response.data.code === 200) {
          alert(`素材提交完成！成功提交 ${total} 个素材`);
        } else {
          alert('素材提交失败，请检查网络或联系管理员');
        }
        
        // 清空已提交的素材信息
        this.successfulMaterials = [];
        
      } catch (error) {
        console.error('一次性提交过程中出现错误:', error);
        alert('素材提交过程中出现错误');
      }
    },
    getCardNumberHeaders() {
      const value = String(this.cardNumber || localStorage.getItem('cardNumber') || localStorage.getItem('username') || '')
      return {
        cardnumber: value,
        cardNumber: value
      }
    },
    
  }
}
</script>

<style scoped>
.upload-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  position: relative;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}.upload-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px 0;
  position: relative;
  z-index: 10;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-5px);
}

.back-icon {
  font-size: 16px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.account-id-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto 30px;
  max-width: 600px;
  position: relative;
  z-index: 20; /* 增加z-index确保在背景之上 */
}

.current-shop-section {
  background: linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto 20px;
  max-width: 600px;
  position: relative;
  z-index: 20; /* 增加z-index确保在背景之上 */
}

.upload-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  position: relative;
  z-index: 20; /* 增加z-index确保在背景之上 */
}

/* 当前授权店铺信息样式 */
.current-shop-section {
  background: linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto 20px;
  max-width: 600px;
}

.shop-info-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.shop-icon {
  font-size: 32px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.shop-details h3 {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 5px 0;
}

.shop-advertiser {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.account-id-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto 30px;
  max-width: 600px;
}

.account-id-container {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

/* 水平排列的输入框组样式 */
.input-group-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto 30px;
  max-width: 1000px;
}

.input-group-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 200px;
}

.input-group-label {
  color: white;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.input-group-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  width: 100%;
  transition: all 0.3s ease;
}

.input-group-input:focus {
  outline: none;
  border-color: #667eea;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-group-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-group-select {
  width: 100%;
}

::v-deep .input-group-select .el-input {
  width: 100%;
}

::v-deep .input-group-select .el-input__inner {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  height: 44px;
  line-height: 44px;
  padding: 0 34px 0 16px;
  transition: all 0.3s ease;
}

::v-deep .input-group-select .el-input.is-focus .el-input__inner,
::v-deep .input-group-select .el-input__inner:focus {
  border-color: #667eea;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

::v-deep .input-group-select .el-input__suffix-inner i {
  color: rgba(255, 255, 255, 0.75);
}

::v-deep .input-group-select.is-disabled .el-input__inner {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.55);
}

::v-deep .input-group-select .el-select-dropdown {
  border-radius: 10px;
}

::v-deep .el-select-dropdown__item {
  color: #1f2a44;
}

.input-group-input[readonly] {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.92);
  cursor: default;
}

.input-group-hint {
  color: #ff4757;
  font-size: 11px;
  font-weight: 500;
}

.account-id-label {
  color: white;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.account-id-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  min-width: 250px;
  transition: all 0.3s ease;
}

.account-id-input:focus {
  outline: none;
  border-color: #667eea;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.account-id-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.account-id-hint {
  color: #ff4757;
  font-size: 12px;
  font-weight: 500;
}

/* 上传方式选择区域样式 */
.upload-methods-section {
  margin: 0 auto 30px;
  max-width: 1200px;
  padding: 0 20px;
  position: relative;
  z-index: 20;
}

.methods-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 30px;
}

.methods-title {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 30px 0;
  text-align: center;
}

.methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 30px;
}

.method-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 25px;
  transition: all 0.3s ease;
  height: fit-content;
}

.method-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.file-upload-card {
  border-left: 4px solid #667eea;
}

.folder-upload-card {
  border-left: 4px solid #56ccf2;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-icon {
  font-size: 32px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.folder-upload-card .card-icon {
  background: linear-gradient(45deg, #56ccf2, #50c878);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.card-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.card-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0 0 20px 0;
  text-align: center;
}

.folder-input-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.folder-path-label {
  color: white;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.folder-path-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  width: 100%;
  transition: all 0.3s ease;
}

.folder-path-input:focus {
  outline: none;
  border-color: #56ccf2;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(86, 204, 242, 0.1);
}

.folder-path-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.folder-upload-button {
  background: linear-gradient(45deg, #56ccf2, #50c878);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.folder-upload-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(86, 204, 242, 0.3);
}

.folder-upload-button:disabled {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.folder-hint {
  color: #56ccf2;
  font-size: 12px;
  font-weight: 500;
  margin-top: 5px;
}

/* 文件上传区域样式 */
.file-input-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.drop-zone {
  border: 2px dashed rgba(102, 126, 234, 0.5);
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(102, 126, 234, 0.05);
}

.drop-zone:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  transform: translateY(-2px);
}

.drop-zone.dragover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.15);
  transform: scale(1.02);
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.drop-icon {
  font-size: 36px;
  color: #667eea;
  margin-bottom: 5px;
}

.drop-zone h4 {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.drop-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin: 0;
}

.file-upload-button {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.file-upload-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
}

.file-upload-button:disabled {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.file-list-section {
  margin: 20px 0;
}

.file-list-title {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.file-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.file-item.uploading {
  border-left: 3px solid #56ccf2;
}

.file-item.success {
  border-left: 3px solid #50c878;
}

.file-item.error {
  border-left: 3px solid #ff4757;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin: 0;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-progress {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  min-width: 80px;
}

.progress-text {
  color: white;
  font-size: 12px;
  font-weight: 500;
}

.status-icon {
  font-size: 16px;
  min-width: 20px;
  text-align: center;
}

.remove-button {
  background: rgba(255, 71, 87, 0.2);
  color: #ff4757;
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.remove-button:hover:not(:disabled) {
  background: rgba(255, 71, 87, 0.3);
  transform: translateY(-1px);
}

.remove-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.upload-progress-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.folder-path-label {
  color: white;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.folder-path-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  min-width: 250px;
  transition: all 0.3s ease;
}

.folder-path-input:focus {
  outline: none;
  border-color: #56ccf2;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(86, 204, 242, 0.1);
}

.folder-path-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.folder-upload-button {
  background: linear-gradient(45deg, #56ccf2, #50c878);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.folder-upload-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(86, 204, 242, 0.3);
}

.folder-upload-button:disabled {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.folder-hint {
  color: #56ccf2;
  font-size: 12px;
  font-weight: 500;
  width: 100%;
  margin-top: 5px;
}

.upload-section {
  margin-bottom: 40px;
}

.upload-area {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  max-height: 400px; /* 限制最大高度 */
}

.upload-area:hover {
  border-color: rgba(102, 126, 234, 0.5);
  background: rgba(255, 255, 255, 0.15);
}

.upload-area.drag-over {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  transform: scale(1.02);
}

.upload-placeholder {
  max-width: 400px;
  margin: 0 auto;
}

.upload-icon {
  font-size: 64px;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.8);
}

.upload-title {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 10px 0;
}

.upload-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  margin: 0 0 25px 0;
}

.select-button {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 15px;
}

.select-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
}

.upload-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin: 0;
}

.file-list {
  text-align: left;
  max-height: 300px; /* 限制文件列表最大高度 */
  overflow-y: auto; /* 添加垂直滚动条 */
  margin: 0 -20px; /* 负边距抵消内边距 */
  padding: 0 20px; /* 恢复内边距 */
}

.file-list::-webkit-scrollbar {
  width: 6px;
}

.file-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.file-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.file-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.file-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.file-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.file-item-wrapper {
  margin-bottom: 10px;
}

.progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  margin-top: 5px;
}

.progress-bar-inner {
  height: 100%;
  background: #2ed573;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.file-icon {
  font-size: 24px;
  color: #667eea;
}

.file-info {
  flex: 1;
}

.file-name {
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 5px 0;
  word-break: break-all;
}

.file-size {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin: 0;
}

.file-status {
  min-width: 80px;
  text-align: center;
}

.uploading-status {
  color: #667eea;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.uploaded-status {
  color: #2ed573;
  font-size: 12px;
}

.error-status {
  color: #ff4757;
  font-size: 12px;
}

.pending-status {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.remove-button {
  background: rgba(255, 71, 87, 0.2);
  color: #ff4757;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-button:hover {
  background: rgba(255, 71, 87, 0.3);
  transform: scale(1.1);
}

.upload-actions {
  display: flex;
  gap: 15px;
  margin-top: 20px;
  justify-content: center;
}

.upload-button {
  background: linear-gradient(45deg, #2ed573, #1dd1a1);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 30px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(46, 213, 115, 0.3);
}

.upload-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-button {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 12px 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.clear-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.clear-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 上传统计信息区域样式 */
.upload-summary-section {
  margin: 0 auto 30px;
  max-width: 800px;
  padding: 0 20px;
  position: relative;
  z-index: 20;
}

.summary-container {
  background: linear-gradient(45deg, rgba(46, 213, 115, 0.1), rgba(29, 209, 161, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(46, 213, 115, 0.3);
  border-radius: 15px;
  padding: 25px;
}

.summary-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
}

.summary-item {
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.summary-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 5px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  color: white;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.summary-time {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin: 0;
}

.loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.video-library {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 30px;
}

.library-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 25px 0;
  text-align: center;
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.video-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 15px;
  transition: all 0.3s ease;
}

.video-card:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-5px);
}

.video-thumbnail {
  position: relative;
  margin-bottom: 15px;
}

.thumbnail-placeholder {
  width: 100%;
  height: 120px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
}

.video-duration {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.video-name {
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
  word-break: break-all;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.video-size, .video-date {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.video-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  flex: 1;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-button.preview {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.action-button.preview:hover {
  background: rgba(102, 126, 234, 0.3);
}

.action-button.delete {
  background: rgba(255, 71, 87, 0.2);
  color: #ff4757;
  border: 1px solid rgba(255, 71, 87, 0.3);
}

.action-button.delete:hover {
  background: rgba(255, 71, 87, 0.3);
}

.empty-library {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 20px;
}

.empty-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 10px 0;
}

.empty-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
}

.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.upload-waves {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(45deg, transparent 40%, rgba(102, 126, 234, 0.05) 50%, transparent 60%),
    linear-gradient(-45deg, transparent 40%, rgba(102, 126, 234, 0.05) 50%, transparent 60%);
  background-size: 60px 60px;
  animation: wave-move 20s linear infinite;
}

@keyframes wave-move {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  100% {
    transform: translate(60px, 60px) rotate(360deg);
  }
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
  animation: float 12s infinite ease-in-out;
}

.element-1 {
  width: 80px;
  height: 80px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.element-2 {
  width: 120px;
  height: 120px;
  top: 60%;
  right: 15%;
  animation-delay: 4s;
}

.element-3 {
  width: 60px;
  height: 60px;
  bottom: 25%;
  left: 70%;
  animation-delay: 8s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-30px) rotate(180deg);
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }
  
  .account-id-container {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .account-id-input {
    min-width: auto;
    width: 100%;
  }
  
  /* 水平排列输入框的响应式样式 */
  .input-group-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .input-group {
    min-width: auto;
    width: 100%;
  }
  
  .library-grid {
    grid-template-columns: 1fr;
  }
  
  .upload-actions {
    flex-direction: column;
  }
  
  .upload-area {
    padding: 40px 20px;
  }
}

@media (max-width: 480px) {
  .video-card {
    padding: 12px;
  }
  
  .video-actions {
    flex-direction: column;
    gap: 5px;
  }
  
  .action-button {
    padding: 8px 12px;
  }
}
</style>
